import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
  Popconfirm,
  Tag,
  DatePicker,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ShareAltOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ToolResultCard } from '@/components/ai-agent/ToolResultCard';
import { useAiAgent } from '@/contexts/AiAgentContext';
import {
  createSavedReport,
  createWorkspaceShare,
  deleteSavedReport,
  downloadAssistantTablePdf,
  downloadAssistantTableXlsx,
  fetchSavedReport,
  fetchSavedReports,
  fetchUdqIntents,
  previewUdq,
  runSavedReport,
  updateSavedReport,
  type AiSavedReport,
  type AssistantResponseLike,
  type UdqIntentCatalogItem,
} from '@/services/aiAgent.service';
import { userService } from '@/services/user.service';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const EXAMPLE_PROMPTS = [
  'Show me all open invoices over 90 days for customers in Germany',
  'Show overdue customer invoices',
  'Show accepted sales quotations month wise for 2026 as a graph',
  'Show AR aging buckets',
  'Show supplier performance list',
  'Show recent inventory movements',
  'Which work orders are delayed?',
];

const FILTER_LABELS: Record<string, string> = {
  period: 'Period',
  dateFrom: 'Date from',
  dateTo: 'Date to',
  asOnDate: 'As-on date',
  topN: 'Top N',
  minOverdueDays: 'Min overdue days',
  minAmount: 'Min amount',
  months: 'Months',
  year: 'Year',
  days: 'Days lookback',
  dueSoonDays: 'Due-soon days',
  customerId: 'Customer ID',
  itemId: 'Item ID',
  warehouseId: 'Warehouse ID',
  supplierId: 'Supplier ID',
  ids: 'Selected IDs (comma-separated)',
  country: 'Country',
};

const PERIOD_OPTIONS = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
];

export default function AiUdqBuilderPage() {
  const { id: routeId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pageContext } = useAiAgent();

  const [mode, setMode] = useState<'library' | 'builder'>(routeId || searchParams.get('new') ? 'builder' : 'library');
  const [reports, setReports] = useState<AiSavedReport[]>([]);
  const [intents, setIntents] = useState<UdqIntentCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [nlQuery, setNlQuery] = useState('');
  const [intentId, setIntentId] = useState<string | undefined>();
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [displayColumns, setDisplayColumns] = useState<string[]>([]);
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(routeId || null);
  const [preview, setPreview] = useState<AssistantResponseLike | null>(null);
  const [schedule, setSchedule] = useState<Record<string, unknown> | null>(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [users, setUsers] = useState<Array<{ id: string; label: string }>>([]);

  const selectedIntent = useMemo(
    () => intents.find((i) => i.id === intentId),
    [intents, intentId]
  );

  const refreshLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([fetchSavedReports(), fetchUdqIntents()]);
      setReports(r);
      setIntents(i);
    } catch {
      message.error('Failed to load UDQs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshLibrary();
  }, [refreshLibrary]);

  useEffect(() => {
    if (!routeId) return;
    setMode('builder');
    setEditingId(routeId);
    void (async () => {
      try {
        const r = await fetchSavedReport(routeId);
        setReportName(r.reportName);
        setDescription(r.description || '');
        setNlQuery(r.query || '');
        setIntentId(r.intent || undefined);
        setFilters(r.filters || {});
        setDisplayColumns(r.displayColumns || []);
        setSchedule(r.schedule ?? null);
        const result = await runSavedReport(routeId);
        setPreview(result);
        if (result.dataTable?.columns?.length && !r.displayColumns?.length) {
          setDisplayColumns(result.dataTable.columns.map((c) => c.key));
        }
      } catch {
        message.error('Failed to open UDQ');
      }
    })();
  }, [routeId]);

  // Prefill from chat "Save as UDQ" deep link and auto-preview
  useEffect(() => {
    const intent = searchParams.get('intent');
    const q = searchParams.get('q');
    const auto = searchParams.get('new') === '1' || !!intent || !!q;
    if (!intent && !q) return;
    setMode('builder');
    if (intent) setIntentId(intent);
    if (q) setNlQuery(q);
    let parsedFilters: Record<string, unknown> = {};
    try {
      const f = searchParams.get('filters');
      if (f) {
        parsedFilters = JSON.parse(f) as Record<string, unknown>;
        setFilters(parsedFilters);
      }
    } catch {
      // ignore
    }
    if (!auto) return;
    let cancelled = false;
    setPreviewLoading(true);
    void previewUdq({
      query: q || undefined,
      intent: intent || undefined,
      filters: parsedFilters,
    })
      .then((out) => {
        if (cancelled) return;
        setIntentId(out.intent);
        setFilters(out.filters);
        setPreview(out.result);
        if (out.result.dataTable?.columns?.length) {
          setDisplayColumns(out.result.dataTable.columns.map((c) => c.key));
        }
        if (out.result.dataTable?.title) {
          setReportName(out.result.dataTable.title);
        }
      })
      .catch((e) => {
        if (!cancelled) message.error(e instanceof Error ? e.message : 'Preview failed');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const sourceContext = useMemo(() => {
    const sel = pageContext.selection;
    if (!sel?.ids?.length && !sel?.listFilters && !pageContext.module) return null;
    return {
      module: pageContext.module,
      docType: pageContext.docType || sel?.entityType,
      selectedIds: sel?.ids || [],
      listFilters: sel?.listFilters,
    };
  }, [pageContext]);

  const runPreview = async () => {
    if (!nlQuery.trim() && !intentId) {
      message.warning('Enter a question or pick an intent from the catalog, then Preview.');
      return;
    }
    setPreviewLoading(true);
    try {
      // Do not send internal keys echoed from prior preview
      const cleanFilters = { ...filters };
      delete cleanFilters.__maxRows;
      delete cleanFilters.__sourceModule;
      delete cleanFilters.__sourceDocType;
      const out = await previewUdq({
        query: nlQuery.trim() || undefined,
        intent: intentId,
        filters: cleanFilters,
        displayColumns: displayColumns.length ? displayColumns : undefined,
        sourceContext,
      });
      setIntentId(out.intent);
      setFilters(out.filters);
      setPreview(out.result);
      if (out.result.dataTable?.columns?.length && !displayColumns.length) {
        setDisplayColumns(out.result.dataTable.columns.map((c) => c.key));
      }
      if (!reportName.trim() && out.result.dataTable?.title) {
        setReportName(out.result.dataTable.title);
      }
      message.success(out.matchedFromNl ? 'Matched natural language to approved query' : 'Preview ready');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveUdq = async () => {
    if (!reportName.trim()) {
      message.warning('Name is required');
      return;
    }
    if (!intentId) {
      message.warning('Run a preview or pick an intent first');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        reportName: reportName.trim(),
        description: description || null,
        query: nlQuery || undefined,
        intent: intentId,
        filters,
        displayColumns: displayColumns.length ? displayColumns : undefined,
        sourceContext,
        outputType: 'table',
        schedule,
      };
      if (editingId) {
        await updateSavedReport(editingId, payload);
        message.success('UDQ updated');
      } else {
        const created = await createSavedReport(payload);
        setEditingId(created.id);
        message.success('UDQ saved');
        navigate(`/ai/udq/${created.id}`, { replace: true });
      }
      await refreshLibrary();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openShare = async () => {
    if (!editingId) {
      message.info('Save the UDQ before sharing');
      return;
    }
    try {
      const list = await userService.getAll({ is_active: true });
      setUsers(
        list.map((u) => ({
          id: u.id,
          label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.username,
        }))
      );
    } catch {
      setUsers([]);
    }
    setShareOpen(true);
  };

  const confirmShare = async () => {
    if (!editingId || !shareUserId) return;
    try {
      await createWorkspaceShare({
        resourceType: 'report',
        resourceId: editingId,
        sharedWithUserId: shareUserId,
        permission: 'view',
      });
      message.success('UDQ shared');
      setShareOpen(false);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Share failed');
    }
  };

  const filterSchema = selectedIntent?.filtersSchema || [];

  const renderFilterField = (key: string) => {
    if (key === 'period') {
      return (
        <Form.Item key={key} label={FILTER_LABELS[key] || key}>
          <Select
            allowClear
            options={PERIOD_OPTIONS}
            value={filters.period as string | undefined}
            onChange={(v) => setFilters((f) => ({ ...f, period: v }))}
            style={{ minWidth: 180 }}
          />
        </Form.Item>
      );
    }
    if (key === 'asOnDate' || key === 'dateFrom' || key === 'dateTo') {
      const val = filters[key];
      return (
        <Form.Item key={key} label={FILTER_LABELS[key] || key}>
          <DatePicker
            value={typeof val === 'string' && val ? dayjs(val) : null}
            onChange={(d) =>
              setFilters((f) => ({ ...f, [key]: d ? d.format('YYYY-MM-DD') : undefined }))
            }
          />
        </Form.Item>
      );
    }
    if (
      key === 'topN' ||
      key === 'minOverdueDays' ||
      key === 'minAmount' ||
      key === 'months' ||
      key === 'year' ||
      key === 'days' ||
      key === 'dueSoonDays'
    ) {
      return (
        <Form.Item key={key} label={FILTER_LABELS[key] || key}>
          <InputNumber
            value={typeof filters[key] === 'number' ? (filters[key] as number) : undefined}
            onChange={(v) => setFilters((f) => ({ ...f, [key]: v ?? undefined }))}
            style={{ width: 140 }}
          />
        </Form.Item>
      );
    }
    return (
      <Form.Item key={key} label={FILTER_LABELS[key] || key}>
        <Input
          value={filters[key] != null ? String(filters[key]) : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (key === 'ids') {
              setFilters((f) => ({
                ...f,
                ids: v
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              }));
            } else {
              setFilters((f) => ({ ...f, [key]: v || undefined }));
            }
          }}
        />
      </Form.Item>
    );
  };

  if (mode === 'library') {
    return (
      <div className="ai-workspace-main" style={{ overflow: 'auto' }}>
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              AI UDQ Builder
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Natural language → approved ERP queries → live custom tables. No raw SQL.
            </Paragraph>
          </div>
          <Space>
            <Link to="/ai/workspace">
              <Button>Workspace</Button>
            </Link>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setMode('builder');
                setEditingId(null);
                setPreview(null);
                setNlQuery('');
                setIntentId(undefined);
                setFilters({});
                setDisplayColumns([]);
                setReportName('');
                setDescription('');
                setSchedule(null);
                navigate('/ai/udq?new=1');
              }}
            >
              New UDQ
            </Button>
          </Space>
        </Space>
        <Card loading={loading}>
          {reports.length === 0 ? (
            <Empty description="No saved UDQs yet">
              <Space direction="vertical" align="center">
                <Button type="primary" onClick={() => {
                  setMode('builder');
                  navigate('/ai/udq?new=1');
                }}>
                  Create your first UDQ
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Example: “Show overdue customer invoices”
                </Text>
              </Space>
            </Empty>
          ) : (
            <Table
              rowKey="id"
              dataSource={reports}
              pagination={{ pageSize: 15 }}
              columns={[
                {
                  title: 'Name',
                  dataIndex: 'reportName',
                  render: (name: string, row: AiSavedReport) => (
                    <Link to={`/ai/udq/${row.id}`}>{name}</Link>
                  ),
                },
                {
                  title: 'Intent',
                  dataIndex: 'intent',
                  render: (v: string | null) => (v ? <Tag>{v}</Tag> : '—'),
                },
                {
                  title: 'Query',
                  dataIndex: 'query',
                  ellipsis: true,
                  render: (v: string | null) => v || '—',
                },
                {
                  title: 'Last run',
                  dataIndex: 'lastRunAt',
                  width: 160,
                  render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
                },
                {
                  title: 'Actions',
                  width: 200,
                  render: (_: unknown, row: AiSavedReport) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        icon={<PlayCircleOutlined />}
                        onClick={() => navigate(`/ai/udq/${row.id}`)}
                      >
                        Open
                      </Button>
                      <Popconfirm
                        title="Delete this UDQ?"
                        onConfirm={() =>
                          void deleteSavedReport(row.id)
                            .then(() => {
                              message.success('Deleted');
                              return refreshLibrary();
                            })
                            .catch(() => message.error('Delete failed'))
                        }
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>
      </div>
    );
  }

  return (
    <div className="ai-workspace-main" style={{ overflow: 'auto' }}>
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {editingId ? 'Edit UDQ' : 'Compose UDQ'}
          </Title>
          <Text type="secondary">Ask in plain language, refine filters, save as a reusable table.</Text>
        </div>
        <Space wrap>
          <Button
            onClick={() => {
              setMode('library');
              navigate('/ai/udq');
            }}
          >
            Library
          </Button>
          <Button icon={<ScheduleOutlined />} onClick={() => setScheduleOpen(true)}>
            Schedule
          </Button>
          <Button icon={<ShareAltOutlined />} onClick={() => void openShare()}>
            Share
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void saveUdq()}>
            Save UDQ
          </Button>
        </Space>
      </Space>

      {sourceContext?.selectedIds?.length ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Text>
            Context: <Tag color="blue">{sourceContext.selectedIds.length} selected</Tag>
            {sourceContext.docType ? <Tag>{String(sourceContext.docType)}</Tag> : null}
            {sourceContext.module ? <Tag>{String(sourceContext.module)}</Tag> : null}
          </Text>
        </Card>
      ) : null}

      <Card title="1. Natural language" style={{ marginBottom: 16 }}>
        <TextArea
          rows={3}
          placeholder='e.g. Show me all open invoices over 90 days for customers in Germany'
          value={nlQuery}
          onChange={(e) => setNlQuery(e.target.value)}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Try an example:
          </Text>
          <Space size={[6, 6]} wrap style={{ marginTop: 6 }}>
            {EXAMPLE_PROMPTS.map((p) => (
              <Tag
                key={p}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setNlQuery(p);
                  setIntentId(undefined);
                }}
              >
                {p.length > 48 ? `${p.slice(0, 48)}…` : p}
              </Tag>
            ))}
          </Space>
        </div>
        <Space style={{ marginTop: 12 }} wrap>
          <Button type="primary" loading={previewLoading} onClick={() => void runPreview()}>
            Preview
          </Button>
          <Select
            showSearch
            allowClear
            placeholder="Or pick an approved intent"
            style={{ minWidth: 280 }}
            options={intents.map((i) => ({ value: i.id, label: `${i.label} (${i.id})` }))}
            value={intentId}
            onChange={(v) => setIntentId(v)}
            filterOption={(input, opt) =>
              String(opt?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Space>
      </Card>

      <Card title="2. Guided refine" style={{ marginBottom: 16 }}>
        {selectedIntent ? (
          <Paragraph type="secondary">{selectedIntent.shortDescription}</Paragraph>
        ) : (
          <Paragraph type="secondary">Run preview or select an intent to edit filters.</Paragraph>
        )}
        <Form layout="inline" style={{ rowGap: 8 }}>
          {filterSchema.map((k) => renderFilterField(k))}
        </Form>
        {preview?.dataTable?.columns?.length ? (
          <div style={{ marginTop: 16 }}>
            <Text strong>Columns</Text>
            <div style={{ marginTop: 8 }}>
              <Checkbox.Group
                options={preview.dataTable.columns.map((c) => ({ label: c.label, value: c.key }))}
                value={displayColumns}
                onChange={(v) => setDisplayColumns(v as string[])}
              />
            </div>
          </div>
        ) : null}
        <Space style={{ marginTop: 16 }} wrap>
          <Input
            placeholder="UDQ name"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            style={{ width: 280 }}
          />
          <Input
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: 320 }}
          />
          <Button loading={previewLoading} onClick={() => void runPreview()}>
            Refresh preview
          </Button>
        </Space>
      </Card>

      <Card
        title="3. Preview table"
        extra={
          preview?.dataTable?.rows?.length ? (
            <Space size={4}>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  void downloadAssistantTableXlsx({
                    intent: preview.intent,
                    filters: preview.sourceContext?.filters as Record<string, unknown> | undefined,
                    dataTable: preview.dataTable,
                    filenameBase: reportName || preview.dataTable?.title || 'udq-export',
                  }).catch((e) => message.error(e instanceof Error ? e.message : 'Export failed'))
                }
              >
                Excel
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  void downloadAssistantTablePdf({
                    intent: preview.intent,
                    filters: preview.sourceContext?.filters as Record<string, unknown> | undefined,
                    dataTable: preview.dataTable,
                    filenameBase: reportName || preview.dataTable?.title || 'udq-export',
                  }).catch((e) => message.error(e instanceof Error ? e.message : 'Export failed'))
                }
              >
                PDF
              </Button>
            </Space>
          ) : null
        }
      >
        {preview ? (
          <ToolResultCard toolName="queryERPData" data={preview} />
        ) : (
          <Empty description="Run preview to see live company data" />
        )}
      </Card>

      <Modal
        title="Share UDQ"
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        onOk={() => void confirmShare()}
        okButtonProps={{ disabled: !shareUserId }}
      >
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select user"
          options={users.map((u) => ({ value: u.id, label: u.label }))}
          value={shareUserId || undefined}
          onChange={setShareUserId}
          filterOption={(input, opt) =>
            String(opt?.label || '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </Modal>

      <Modal
        title="Schedule UDQ"
        open={scheduleOpen}
        onCancel={() => setScheduleOpen(false)}
        onOk={() => {
          setScheduleOpen(false);
          message.success('Schedule stored — save the UDQ to persist');
        }}
      >
        <Form layout="vertical">
          <Form.Item label="Frequency">
            <Select
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
              ]}
              value={(schedule?.frequency as string) || 'weekly'}
              onChange={(frequency) => setSchedule((s) => ({ ...(s || {}), frequency }))}
            />
          </Form.Item>
          <Form.Item label="Email to">
            <Input
              value={(schedule?.emailTo as string) || ''}
              onChange={(e) => setSchedule((s) => ({ ...(s || {}), emailTo: e.target.value }))}
              placeholder="colleague@company.com"
            />
          </Form.Item>
          <Form.Item label="Hour (UTC)">
            <InputNumber
              min={0}
              max={23}
              value={typeof schedule?.hourUtc === 'number' ? schedule.hourUtc : 6}
              onChange={(hourUtc) => setSchedule((s) => ({ ...(s || {}), hourUtc: hourUtc ?? 6 }))}
            />
          </Form.Item>
          <Button danger type="link" onClick={() => setSchedule(null)}>
            Clear schedule
          </Button>
        </Form>
      </Modal>
    </div>
    </div>
  );
}
